import { type ReactNode } from 'react';
import { Coin } from '@hungryroot/ui-kit';
// Image media (search/cookbook/orders) is served from the public dir and
// referenced by URL rather than imported, so the legacy webpack signup build —
// which has no PNG asset loader — can compile this module. Lottie animations
// (welcome/discover/credits) are loaded by PopsicleLottie via dynamic JSON
// import (which the build does handle).
import { type PopsicleAnimationKey } from './PopsicleLottie';
import styles from './stages.module.css';

// Must match the `testid` passed to <MobileNav> in MobileFlexibleHeaderShell.
// Each floating-nav tab renders `data-testid="{NAV_TESTID}-link-{tab.key}"`.
export const NAV_TESTID = 'spa-container-mobile-nav';

/**
 * Selector for one floating-nav tab. Every `FloatingBottomNav` tab renders
 * `data-testid="{navTestid}-link-{tabKey}"`, so a tour on any nav — the
 * customer nav or the Exp 255 guest nav — only has to name its nav and keys.
 */
export function navTabSelector(navTestid: string, tabKey: string): string {
  return `[data-testid="${navTestid}-link-${tabKey}"]`;
}

const HOME_TAB_SELECTOR = navTabSelector(NAV_TESTID, 'home');
const SEARCH_TAB_SELECTOR = navTabSelector(NAV_TESTID, 'search');
// The Cookbook tab's key is `favorites` in MobileNavFloating.
const COOKBOOK_TAB_SELECTOR = navTabSelector(NAV_TESTID, 'favorites');
const ORDERS_TAB_SELECTOR = navTabSelector(NAV_TESTID, 'orders');

// reactour requires a selector per step. The V2 welcome step highlights nothing,
// so it points at an offscreen anchor → the cutout falls offscreen → the whole
// viewport stays dimmed.
export const POPSICLE_TOUR_ANCHOR_ATTR = 'data-popsicle-tour-anchor';

// The V2 tour ends on a coachmark pointing at the profile avatar rendered by
// MobileHomeTopHeader. That avatar is a DropdownPopover whose Ramen Button
// trigger renders `data-testid="{testid}-{buttonTestId}-trigger"`. reactour
// spotlights this selector and the tooltip anchors to it.
export const PROFILE_TOOLTIP_TESTID = 'mobile-home-top-header-profile-trigger';
export const PROFILE_TOOLTIP_SELECTOR = `[data-testid="${PROFILE_TOOLTIP_TESTID}"]`;

// V1: the first-order landing decides the final (credits) step's spotlight —
// "keep exploring" → `/myhungryroot/` ('home'), "edit order" → `/orders/`
// ('orders'). Every other step is identical between the two.
export type PopsicleTourFlow = 'home' | 'orders';

export type PopsicleStage = {
  // Stable identity for React keys (media may be an image, so `animationKey`
  // can't double as a key).
  id: string;
  // Media is either a Lottie animation or a static image served from the public
  // dir. Exactly one of these is set.
  animationKey?: PopsicleAnimationKey;
  imageSrc?: string;
  loop: boolean;
  heading: ReactNode;
  body: ReactNode;
  ctaLabel: string;
  selector: string;
};

// --- Shared modules (used by the V1 and V2 tab walkthroughs and the Exp 255
// guest feed tour). Each takes the nav-tab selector it spotlights, since the
// customer and guest navs render under different testids. ---

type StageOverrides = Partial<Pick<PopsicleStage, 'ctaLabel'>>;

function buildDiscoverStage(
  selector: string,
  overrides: StageOverrides = {}
): PopsicleStage {
  return {
    id: 'discover',
    animationKey: 'discover',
    loop: false,
    heading: 'Discover new favorites',
    body: 'As you ❤️ recipes, we’ll learn what you like and personalize future orders around your preferences.',
    ctaLabel: 'Continue',
    selector,
    ...overrides,
  };
}

function buildSearchStage(
  selector: string,
  overrides: StageOverrides = {}
): PopsicleStage {
  return {
    id: 'search',
    imageSrc: '/public/img/popsicle-tour/search.png',
    loop: false,
    heading: 'Search for what you like',
    body: 'Search recipes, meals, and groceries anytime. With 50,000+ options, there’s always something new to discover.',
    ctaLabel: 'Continue',
    selector,
    ...overrides,
  };
}

function buildCookbookStage(
  selector: string,
  overrides: StageOverrides = {}
): PopsicleStage {
  return {
    id: 'cookbook',
    imageSrc: '/public/img/popsicle-tour/cookbook.png',
    loop: false,
    heading: 'Build your cookbook',
    body: 'Everything you ❤️ is saved in one place. The more you save, the better we can personalize future orders.',
    ctaLabel: 'Continue',
    selector,
    ...overrides,
  };
}

const discoverStage = buildDiscoverStage(HOME_TAB_SELECTOR);
const searchStage = buildSearchStage(SEARCH_TAB_SELECTOR);
const cookbookStage = buildCookbookStage(COOKBOOK_TAB_SELECTOR);

// --- V1 (new-user, first-order) walkthrough: discover → search → cookbook →
// orders → credits. Only the final credits step differs by landing page. ---

const manageOrdersStage: PopsicleStage = {
  id: 'orders',
  imageSrc: '/public/img/popsicle-tour/orders.png',
  loop: false,
  heading: 'Manage orders',
  body: 'Review and edit this week’s cart, manage upcoming deliveries, and see past orders anytime.',
  ctaLabel: 'Continue',
  selector: ORDERS_TAB_SELECTOR,
};

function creditsStage(flow: PopsicleTourFlow): PopsicleStage {
  return {
    id: 'credits',
    animationKey: 'credits',
    loop: false,
    heading: (
      <span className={styles['credit-amount']}>
        Shop with credits
        <Coin className={styles['credit-icon']} />
      </span>
    ),
    body: 'Think of credits as your weekly food budget. Use them on recipes, groceries, and meals however you like.',
    ctaLabel: 'Ok, got it',
    // "Keep exploring" ends on Home; "Edit order" ends on the Orders tab.
    selector: flow === 'orders' ? ORDERS_TAB_SELECTOR : HOME_TAB_SELECTOR,
  };
}

// --- V2 (existing-user) walkthrough: welcome → discover → search → cookbook →
// orders, then a profile tooltip (rendered by the tour component). ---

const welcomeStage: PopsicleStage = {
  id: 'welcome',
  animationKey: 'welcome',
  loop: false,
  heading: 'Welcome back!',
  body: 'We’ve refreshed Hungryroot to make discovering recipes, shopping groceries, and managing your orders even easier.',
  ctaLabel: 'Explore the new look',
  // The welcome step dims the whole screen (no tab cutout), like credits used to.
  selector: `[${POPSICLE_TOUR_ANCHOR_ATTR}]`,
};

const ordersStageV2: PopsicleStage = {
  id: 'orders',
  imageSrc: '/public/img/popsicle-tour/orders.png',
  loop: false,
  heading: 'Manage orders',
  body: 'Review and edit this week’s cart, manage upcoming deliveries, and see past orders anytime.',
  ctaLabel: 'Start discovering recipes',
  selector: ORDERS_TAB_SELECTOR,
};

export function buildPopsicleStages(flow: PopsicleTourFlow): PopsicleStage[] {
  return [
    discoverStage,
    searchStage,
    cookbookStage,
    manageOrdersStage,
    creditsStage(flow),
  ];
}

export function buildPopsicleStagesV2(): PopsicleStage[] {
  return [
    welcomeStage,
    discoverStage,
    searchStage,
    cookbookStage,
    ordersStageV2,
  ];
}

// --- Exp 255 guest feed tour (`/discover/feed/`): discover → search →
// cookbook, spotlighting the guest nav's tabs. Guests have no orders and no
// credits yet, so neither of those stages is shown; cookbook is the final
// screen and carries the closing CTA. ---

export interface FreemiumFeedStagesOptions {
  /** The guest nav's `testid` (`GuestFloatingBottomNav` passes `guest-mobile-nav`). */
  navTestid: string;
  /**
   * Whether the `/discover/` tab is the search bar (`web.feature-flag.discover-search.enabled`).
   * When it is off the tab reads "Browse" and the search stage's copy would
   * describe a feature that is not there, so the stage is left out.
   */
  includeSearch: boolean;
}

export function buildFreemiumFeedStages({
  navTestid,
  includeSearch,
}: FreemiumFeedStagesOptions): PopsicleStage[] {
  const closingCta: StageOverrides = { ctaLabel: 'Ok, got it' };
  return [
    buildDiscoverStage(navTabSelector(navTestid, 'home')),
    ...(includeSearch
      ? [buildSearchStage(navTabSelector(navTestid, 'search'))]
      : []),
    buildCookbookStage(navTabSelector(navTestid, 'cookbook'), closingCta),
  ];
}
